import { Property } from "@fairfleet/geotab";
import { VehicleCheckResult, VehicleCheckStatus } from "../models/VehicleCheck";
import {
  VEHICLE_CHECK_ALERT_WORDS,
  VEHICLE_CHECK_OK_WORDS,
  VEHICLE_CHECK_WARN_PREFIXES,
} from "@/constants/VehicleCheckWords";

/** Shape of one entry in `Device.customProperties` as returned by the Geotab API. */
interface DeviceCustomProperty {
  property?: { id?: string; name?: string };
  value?: unknown;
}

export function normalizeCheckValue(value: string): string {
  return value.toLocaleLowerCase("de-DE").replace(/\s+/g, " ").trim();
}

export function classifyVehicleCheck(rawValue: string | undefined | null): VehicleCheckResult {
  const value = rawValue == null ? "" : String(rawValue);
  const normalized = normalizeCheckValue(value);
  let status: VehicleCheckStatus;
  if (!normalized) status = "offen";
  else if ((VEHICLE_CHECK_OK_WORDS as readonly string[]).includes(normalized)) status = "ok";
  else if (VEHICLE_CHECK_WARN_PREFIXES.some(prefix => normalized.startsWith(prefix))) status = "warn";
  else if (VEHICLE_CHECK_ALERT_WORDS.some(word => normalized.includes(word))) status = "alert";
  else status = "neutral";
  return { status, value: value.trim() };
}

/**
 * Reads a custom property value by name. Geotab may return the nested property with or
 * without its name, so the id is resolved through the property definitions when needed.
 */
export function readCustomProperty(
  device: { customProperties?: unknown[] },
  names: readonly string[],
  propertiesById: Record<string, Property> = {},
): string | undefined {
  const wanted = names.map(normalizeCheckValue);
  for (const name of wanted) {
    for (const entry of (device.customProperties ?? []) as DeviceCustomProperty[]) {
      const id = entry?.property?.id;
      const propertyName = entry?.property?.name ?? (id ? propertiesById[id]?.name : undefined);
      if (propertyName && normalizeCheckValue(propertyName) === name) {
        return entry.value == null ? undefined : String(entry.value);
      }
    }
  }
  return undefined;
}
